"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getMeterStatus = exports.noSupplyMeterStatus = void 0;
var date_fns_1 = require("date-fns");
var date_fns_tz_1 = require("date-fns-tz");
var constants_1 = require("../../../constants");
var utils_1 = require("../../../utils");
var getLatestActualReading_1 = require("../../../utils/getLatestActualReading");
exports.noSupplyMeterStatus = {
    status: 'error',
    reason: 'noSupply',
    meterType: null,
    nextReadingDate: '',
};
var getMeterStatus = function (_a) {
    var readings = _a.readings, rawMeterType = _a.rawMeterType, timezone = _a.timezone, latestBillingEndDate = _a.latestBillingEndDate;
    var currentDate = (0, date_fns_tz_1.toZonedTime)(new Date(), timezone);
    if (readings.length === 0) {
        return {
            status: 'error',
            reason: 'noReadings',
            meterType: null,
            nextReadingDate: '',
        };
    }
    var meterType = rawMeterType
        ? (0, utils_1.isSmartSupply)(rawMeterType)
            ? 'smart'
            : 'trad'
        : null;
    if (!rawMeterType) {
        return {
            status: 'error',
            reason: 'noMeterType',
            meterType: meterType,
            nextReadingDate: '',
        };
    }
    if (!latestBillingEndDate) {
        return {
            status: 'error',
            reason: 'noBillingEndDate',
            meterType: meterType,
            nextReadingDate: '',
        };
    }
    // If the billing date is in the past, we add a month to it.
    // This accounts for when the actual next billing period has no transactions yet.
    var nextReadingDate = (0, date_fns_1.isBefore)((0, date_fns_tz_1.toDate)(latestBillingEndDate, { timeZone: timezone }), currentDate)
        ? (0, date_fns_tz_1.toDate)((0, date_fns_1.addDays)((0, date_fns_1.addMonths)(latestBillingEndDate, 1), 1), {
            timeZone: timezone,
        })
        : (0, date_fns_tz_1.toDate)((0, date_fns_1.addDays)(latestBillingEndDate, 1), { timeZone: timezone });
    if ((0, date_fns_1.isBefore)(nextReadingDate, currentDate)) {
        return {
            status: 'error',
            reason: 'billingDateInPast',
            meterType: meterType,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
        };
    }
    // Will a smart meter always have an actual read? This might fail too early if we accept latest _smart_ reads
    var lastActual = (0, getLatestActualReading_1.getLatestActualReading)(readings);
    if (!lastActual) {
        return {
            status: 'error',
            reason: 'noLastActual',
            meterType: meterType,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
        };
    }
    var lastReadingDate = lastActual.formattedReadingDate;
    return (0, utils_1.isSmartSupply)(rawMeterType)
        ? handleSmartMeterStatus({
            readings: readings,
            lastReadingDate: lastReadingDate,
            nextReadingDate: nextReadingDate,
            timezone: timezone,
            meterType: meterType,
        })
        : handleTradMeterStatus({
            lastReadingDate: lastReadingDate,
            nextReadingDate: nextReadingDate,
            timezone: timezone,
            meterType: meterType,
        });
};
exports.getMeterStatus = getMeterStatus;
var handleSmartMeterStatus = function (_a) {
    var readings = _a.readings, lastReadingDate = _a.lastReadingDate, nextReadingDate = _a.nextReadingDate, timezone = _a.timezone, meterType = _a.meterType;
    var currentDate = (0, date_fns_tz_1.toZonedTime)(new Date(), timezone);
    var latestSmart = (0, getLatestActualReading_1.getLatestSmartReading)(readings);
    var isSmartNonComm = !latestSmart ||
        Math.abs((0, date_fns_1.differenceInDays)(currentDate, latestSmart.formattedReadingDate)) >=
            5;
    var hasRecentManual = Math.abs((0, date_fns_1.differenceInDays)(currentDate, lastReadingDate)) < 5;
    if (isSmartNonComm && hasRecentManual) {
        return {
            status: 'ok',
            reason: 'recentManual',
            lastReadingDate: lastReadingDate,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
            meterType: meterType,
        };
    }
    if (isSmartNonComm) {
        return {
            status: 'warning',
            reason: 'smartNonComm',
            lastReadingDate: lastReadingDate,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
            meterType: meterType,
        };
    }
    return {
        status: 'ok',
        reason: 'meterok',
        lastReadingDate: lastReadingDate,
        nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
        meterType: meterType,
    };
};
var handleTradMeterStatus = function (_a) {
    var lastReadingDate = _a.lastReadingDate, nextReadingDate = _a.nextReadingDate, timezone = _a.timezone, meterType = _a.meterType;
    var currentDate = (0, date_fns_tz_1.toZonedTime)(new Date(), timezone);
    var isNextReadOverDue = Math.abs((0, date_fns_1.differenceInDays)(currentDate, lastReadingDate)) > 30 &&
        Math.abs((0, date_fns_1.differenceInDays)(nextReadingDate, currentDate)) > 5;
    if (isNextReadOverDue) {
        return {
            status: 'warning',
            reason: 'tradReadingOverDue',
            lastReadingDate: lastReadingDate,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
            meterType: meterType,
        };
    }
    var nextReadingWindowStart = (0, date_fns_1.subDays)(nextReadingDate, 5);
    // next due when last reading is older than {next reading due date - 5 days} and next reading is <=5 days away
    var isNextMeterReadDue = (0, date_fns_1.isBefore)(lastReadingDate, nextReadingWindowStart) &&
        Math.abs((0, date_fns_1.differenceInDays)(nextReadingDate, currentDate)) <= 5;
    // due but received is when today is in read window and so is the last read
    var isDueButReceived = (0, date_fns_1.isWithinInterval)(lastReadingDate, {
        start: nextReadingWindowStart,
        end: nextReadingDate,
    }) &&
        (0, date_fns_1.isWithinInterval)(currentDate, {
            start: nextReadingWindowStart,
            end: nextReadingDate,
        });
    if (isDueButReceived) {
        return {
            status: 'ok',
            reason: 'isDueButReceived',
            lastReadingDate: lastReadingDate,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
            meterType: meterType,
        };
    }
    if (isNextMeterReadDue) {
        return {
            status: 'due',
            reason: 'tradReadingDue',
            lastReadingDate: lastReadingDate,
            nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
            meterType: meterType,
        };
    }
    return {
        status: 'ok',
        reason: 'meterok',
        lastReadingDate: lastReadingDate,
        nextReadingDate: (0, date_fns_tz_1.format)(nextReadingDate, constants_1.DATE_API_FORMAT),
        meterType: meterType,
    };
};
